import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/update-profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  generateSingleUseToken: () => api.post('/auth/generate-single-use-token'),
  verifySingleUseToken: (token) => api.post('/auth/verify-single-use-token', { token }),
};

// Application API
export const applicationAPI = {
  create: (data) => api.post('/applications', data),
  getAll: () => api.get('/applications'),
  getOne: (id) => api.get(`/applications/${id}`),
  submit: (id) => api.post(`/applications/${id}/submit`),
  delete: (id) => api.delete(`/applications/${id}`),
  checkEligibility: (id) => api.get(`/applications/${id}/eligibility`),
};

// PFS API
export const pfsAPI = {
  create: (data) => api.post('/pfs', data),
  getAll: () => api.get('/pfs'),
  getOne: (id) => api.get(`/pfs/${id}`),
  submit: (id) => api.post(`/pfs/${id}/submit`),
};

// Debt Schedule API
export const debtScheduleAPI = {
  create: (data) => api.post('/debt-schedule', data),
  getAll: () => api.get('/debt-schedule'),
  getOne: (id) => api.get(`/debt-schedule/${id}`),
  submit: (id) => api.post(`/debt-schedule/${id}/submit`),
};

// Document API
export const documentAPI = {
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll: (params) => api.get('/documents', { params }),
  getOne: (id) => api.get(`/documents/${id}`),
  download: (id) => api.get(`/documents/${id}/download`),
  delete: (id) => api.delete(`/documents/${id}`),
  getRequirements: (programId) => api.get(`/documents/requirements/${programId}`),
};

// Program API
export const programAPI = {
  getAll: () => api.get('/programs'),
  getOne: (id) => api.get(`/programs/${id}`),
  checkReadiness: (id) => api.get(`/programs/${id}/readiness`),
  getDashboard: () => api.get('/programs/dashboard/overview'),
};

// E-Sign API
export const esignAPI = {
  send: (type, id) => api.post(`/esign/send/${type}/${id}`),
  getStatus: (type, id) => api.get(`/esign/status/${type}/${id}`),
};

// Integration API
export const integrationAPI = {
  syncLoanHub: (applicationId) => api.post('/integrations/loan-hub/sync', { applicationId }),
  importPFS: (pfsId) => api.post('/integrations/pfs-copilot/import', { pfsId }),
  importApplication: (applicationId) => api.post('/integrations/financing-app/import', { applicationId }),
};

// Admin API
export const adminAPI = {
  getApplications: (params) => api.get('/admin/applications', { params }),
  reviewApplication: (id, data) => api.put(`/admin/applications/${id}/review`, data),
  getPendingDocuments: () => api.get('/admin/documents/pending'),
  reviewDocument: (id, data) => api.put(`/admin/documents/${id}/review`, data),
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  exportUserDocuments: (userId) => api.get(`/admin/users/${userId}/documents/export-all`, { responseType: 'blob' }),
};

// Tax Return Analysis API
export const taxAnalysisAPI = {
  getDSCRSummary: () => api.get('/tax-analysis/dscr-summary'),
  getAll: () => api.get('/tax-analysis'),
  getOne: (id) => api.get(`/tax-analysis/${id}`),
  process: (documentId, data) => api.post(`/tax-analysis/process/${documentId}`, data),
  // Admin
  adminGetAll: (params) => api.get('/tax-analysis/admin/all', { params }),
  adminGetUserDSCR: (userId) => api.get(`/tax-analysis/admin/user/${userId}/dscr-summary`),
  adminUpdate: (id, data) => api.put(`/tax-analysis/admin/${id}`, data),
  adminUploadForUser: (data) => api.post('/tax-analysis/admin/upload-for-user', data),
};

// Bank Statement Analysis API
export const bankAnalysisAPI = {
  getSummary: () => api.get('/bank-analysis/summary'),
  getAll: () => api.get('/bank-analysis'),
  getOne: (id) => api.get(`/bank-analysis/${id}`),
  process: (documentId, data) => api.post(`/bank-analysis/process/${documentId}`, data),
  // Admin
  adminGetAll: (params) => api.get('/bank-analysis/admin/all', { params }),
  adminGetUserSummary: (userId) => api.get(`/bank-analysis/admin/user/${userId}/summary`),
  adminUpdate: (id, data) => api.put(`/bank-analysis/admin/${id}`, data),
  adminUploadForUser: (data) => api.post('/bank-analysis/admin/upload-for-user', data),
};

export default api;
